import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import DataTable from '../shared/DataTable';
import { SearchInput } from '../shared/ListFilters';
import { useUserStore } from '../../store';
import { usePaginatedList } from '../../hooks/usePaginatedList';


function MainServicePackagesView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  const [showArchived, setShowArchived] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const serverFilters = useMemo(() => {
    if (showArchived) return {};
    return { is_active: 'true' };
  }, [showArchived]);

  const {
    data: packages,
    loading,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters,
    refetch,
  } = usePaginatedList('/api/service-packages/', {
    initialFilters: serverFilters,
    defaultSortField: 'name',
    defaultSortDirection: 'asc',
    defaultPageSize: 50,
  });

  useEffect(() => {
    setFilters(serverFilters);
  }, [serverFilters, setFilters]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`/api/service-packages/${toDelete.id}/`, {
        params: { organization: orgId },
      });
      setSnackbar({ severity: 'success', message: t('huoltopaketit.deleted') });
      setToDelete(null);
      refetch();
    } catch (err) {
      setSnackbar({ severity: 'error', message: t('huoltopaketit.errorDeleting') });
    }
  };

  const handleToggleActive = async (pkg, nextValue) => {
    try {
      await axios.patch(
        `/api/service-packages/${pkg.id}/`,
        { is_active: nextValue },
        { params: { organization: orgId } },
      );
      refetch();
    } catch (err) {
      setSnackbar({ severity: 'error', message: t('huoltopaketit.errorSaving') });
    }
  };

  const columns = useMemo(() => [
    {
      key: 'name',
      label: t('huoltopaketit.name'),
      maxWidth: 400,
      render: (row) => (
        <Typography sx={{ fontWeight: 500 }}>{row.name}</Typography>
      ),
    },
    {
      key: 'jobs',
      label: t('huoltopaketit.jobsShort'),
      align: 'right',
      width: '1%',
      render: (row) => row.job_count ?? 0,
    },
    {
      key: 'parts',
      label: t('huoltopaketit.partsShort'),
      align: 'right',
      width: '1%',
      render: (row) => row.part_count ?? 0,
    },
    {
      key: 'hours',
      label: t('huoltopaketit.totalHours'),
      align: 'right',
      width: '1%',
      render: (row) => row.total_hours ? `${row.total_hours} h` : '—',
    },
    {
      key: 'active',
      label: t('huoltopaketit.isActive'),
      align: 'center',
      width: '1%',
      render: (row) => (
        <Switch
          size="small"
          checked={row.is_active}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleToggleActive(row, e.target.checked)}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      width: '1%',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); navigate(`/huoltopaketit/${row.id}`); }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setToDelete(row); }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, navigate]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="service-packages-page">
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t('huoltopaketit.title')}
          </Typography>
          <Typography sx={{ fontSize: '14px', color: 'text.secondary', mt: 0.5 }}>
            {t('huoltopaketit.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/huoltopaketit/new')}
          sx={{ bgcolor: 'secondary.main' }}
          data-testid="add-service-package-button"
        >
          {t('huoltopaketit.new')}
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2.5, alignItems: 'center' }}>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={() => setSearchQuery('')}
          placeholder={t('huoltopaketit.searchPlaceholder')}
          sx={{ flex: 1, minWidth: 220 }}
        />
        <Chip
          label={showArchived ? t('huoltopaketit.showingAll') : t('huoltopaketit.showingActive')}
          variant={showArchived ? 'filled' : 'outlined'}
          onClick={() => setShowArchived((prev) => !prev)}
        />
      </Box>

      <DataTable
        columns={columns}
        data={packages}
        loading={loading}
        alwaysVisible
        onRowClick={(row) => navigate(`/huoltopaketit/${row.id}`)}
        emptyIcon={<Inventory2Icon />}
        emptyTitle={t('huoltopaketit.emptyTitle')}
        emptyDescription={t('huoltopaketit.emptyDescription')}
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)}>
        <DialogTitle>{t('huoltopaketit.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('huoltopaketit.deleteConfirm', { name: toDelete?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>{t('Cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
      >
        {snackbar && <Alert severity={snackbar.severity}>{snackbar.message}</Alert>}
      </Snackbar>
    </Box>
  );
}

export default MainServicePackagesView;
